import React, { useContext, useEffect, useState } from "react"
import tw from "twin.macro"
// import dynamic from "next/dynamic"
import { QuestionProps } from "../../../pages/q/[id]"
import { AuthContext, DomainPath } from "../../../utils/context"
import {
  AnswerDetailFragment,
  QuestionState,
  SupportPaymentState,
  useGetQuestionListNavigationLazyQuery,
  QuestionListNavigation,
  Question,
  useGetNewestOpenQuestionLazyQuery,
} from "../../../graphql/generated/graphql"
// const RedeemNft = dynamic(() => import("../../nftBar/redeemNft"), { ssr: false })
import { Container } from "../../page/gridContainer"
import { PaymentGateModal } from "../../paymentGate"
import { NextOpenQuestion } from "../nextQuestion"
import { Modal } from "../../modal"
import { Events, registerEvent } from "../../../analytics"
import Link from "next/link"
import { Routes } from "../../../routes"
import { useTranslation } from "next-i18next"
import Datetime from "../../datetime"
import { IconSize, Share } from "../../share"
import QAText from "../qaText"
import { DateTime } from "luxon"
import Loading from "../../loading"
import { AnswerCard } from "../../answerCard"
import { Button } from "../../formElements/button"
import { ButtonTheme } from "../../formElements/button"
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync"
import { PreviousNextControl } from "../previousNextControl"

interface VideoPlayerProps {
  Answer: AnswerDetailFragment
  mimeType?: string
}

export const VideoPlayer = (props: VideoPlayerProps) => {
  const { mimeType, Answer } = props
  const { body, coverImage, createdAt, questionId, igShareLink } = Answer
  const shareUrl = `${DomainPath()}/q/${questionId}`
  const hasRenditions = !!Answer?.shareRendition

  return (
    <>
      <div tw="border-base-2 border-2 rounded-lg p-2 space-y-2">
        <video controls tw="w-full rounded" src={body} preload="metadata" poster={coverImage || ""}>
          {mimeType ? <source src={body} type={mimeType} /> : <source src={body} />}
        </video>
        <div data-testid="video-created-at" tw="text-base-3 text-sm flex items-center justify-between">
          <Datetime datetime={createdAt} />
          <span tw="flex items-center space-x-2">
            <Share {...{ shareUrl, igShareLink, shareId: questionId, size: IconSize.W28, hasRenditions }} />
          </span>
        </div>
      </div>
    </>
  )
}

export const QuestionAnswered = (props: QuestionProps) => {
  const { question, user, referringProfileUsername, refetchQuestion } = props
  const { Artist, Answer, createdAt, Fan, body } = question
  const { id, isTakingQuestions } = Artist
  if (!Answer) {
    return <Loading />
  }
  const { userId } = useContext(AuthContext)
  //if we allow one User to have few support payments, we will need to change this logic
  const supportPayment = question.supportPayments.find((supportPayment) => supportPayment.Supporter.id === userId)
  const failedSupportPayment = supportPayment && supportPayment.state === SupportPaymentState.Failed
  const [showPaymentGate, setShowPaymentGate] = useState(false)
  const [answerNavigation, setAnswerNavigation] = useState<QuestionListNavigation>()
  const [newestOpenQuestion, setNewestOpenQuestion] = useState<Question | undefined | null>()
  const [getNewestOpenQuestion] = useGetNewestOpenQuestionLazyQuery()
  const isArtist = userId === id

  const modalProps = {
    show: showPaymentGate,
    setShow: setShowPaymentGate,
    notDismissable: true,
    component:
      user && supportPayment ? (
        <PaymentGateModal {...{ user, artist: Artist, supportPayment, setShowPaymentGate }} />
      ) : (
        <></>
      ),
  }

  useEffect(() => {
    failedSupportPayment && setShowPaymentGate(true)
  }, [])

  useEffect(() => {
    registerEvent(Events.VisitedAnsweredQuestion)
  }, [])

  useEffectAsync(async () => {
    if (isArtist && isTakingQuestions) {
      const res = await getNewestOpenQuestion()
      if (res && res.data && res.data.NewestOpenQuestion) {
        setNewestOpenQuestion(res.data.NewestOpenQuestion as Question)
      }
    }
  }, [])

  // get the navigation data
  const [getNavigation] = useGetQuestionListNavigationLazyQuery()
  useEffectAsync(async () => {
    const answerListNavResponse = await getNavigation({
      variables: {
        username: referringProfileUsername,
        state: QuestionState.Answered,
        currentQuestionId: question.id,
      },
    })
    if (answerListNavResponse && answerListNavResponse.data) {
      setAnswerNavigation(answerListNavResponse.data.QuestionListNavigation as QuestionListNavigation)
    }
  }, [])

  const { t } = useTranslation("question")
  const formatDate = (time: string) => DateTime.fromISO(time).toFormat("MMM dd")

  const questionTextProps = {
    date: formatDate(createdAt),
    username: Fan.username,
    name: Fan.name ? Fan.name : Fan.username,
    isAnswer: false,
    body: body,
  }
  const answerTextProps = {
    date: formatDate(Answer.createdAt),
    username: Artist.username,
    name: Artist.name ? Artist.name : Artist.username,
    isAnswer: true,
    body: Answer.transcript ? Answer.transcript : "Transcription is coming soon!",
  }

  return (
    <section tw="py-8 mb-12 h-full">
      <Container>
        {newestOpenQuestion ? (
          <NextOpenQuestion {...{ question: newestOpenQuestion }} />
        ) : (
          answerNavigation &&
          answerNavigation.TotalCount > 1 && (
            <PreviousNextControl
              {...{
                currentQuestion: question,
                nextQuestionId: answerNavigation.NextQuestion?.id,
                previousQuestionId: answerNavigation.PreviousQuestion?.id,
                index: answerNavigation.Index,
                referringProfileUsername,
              }}
            />
          )
        )}
        <div tw="grid grid-cols-4 tablet:grid-cols-12 gap-4 desktop:gap-6 gap-y-4">
          <div tw="col-span-4 space-y-8 tablet:(col-span-8 col-start-3) tablet-lg:(col-span-8 col-start-3)">
            <h1 tw="py-2 text-xl font-bold text-center">
              {t("answer_from")}
              <Link href={{ pathname: Routes.Profile, query: { username: Artist.username } }} passHref>
                <a data-testid="artist-profile-link" tw="block tablet:(inline)">
                  {Artist.name}
                </a>
              </Link>
            </h1>
          </div>

          {/* 
          DF: DISABLING NFT REMINDERS 
          {supportPaymentExists.length > 0 && !isMinted && (
            <div tw="col-span-4 order-2 pb-1 tablet:col-span-12 tablet-lg:(col-span-8 col-start-3 order-1)">
              <RedeemNft {...props} />
            </div>
          )} */}

          <div tw="col-span-4 order-1 tablet:(col-span-6 order-2) tablet-lg:(col-span-4 col-start-3 order-2)">
            <div tw="flex flex-col space-y-4">
              <AnswerCard
                {...{
                  question,
                  isReactable: true,
                  refetchQuestion,
                  useEmojiSmall: false,
                  isPlayable: true,
                  referringProfileUsername,
                }}
              />

              {!isArtist && (
                <Link href={{ pathname: Routes.Profile, query: { username: Artist.username } }} passHref>
                  <a tw="no-underline" data-testid="ask-question">
                    <Button action={t("ask_question")} theme={ButtonTheme.PrimaryOutline} twStyle={tw`w-full`} />
                  </a>
                </Link>
              )}
            </div>
          </div>

          <div tw="col-span-4 order-3 tablet:(col-span-6 order-3) tablet-lg:(col-span-4 order-3)">
            <div tw="mb-8 space-y-4 tablet:m-0">
              <QAText {...questionTextProps} />
              {<QAText {...answerTextProps} />}
            </div>
          </div>
        </div>
        <Modal {...modalProps} />
      </Container>
    </section>
  )
}
